import { useState, useCallback } from "react";
import { getInitialOptionsState } from "../utils/pathBuilderHelpers";

/**
 * Custom hook for managing PathBuilder options state
 * Handles all option changes and state logic
 */
export function usePathBuilderOptions() {
  const [options, setOptions] = useState(getInitialOptionsState());

  /**
   * Reset all fields except OS selection
   */
  const resetAllFieldsExceptOS = useCallback((prevOptions) => {
    const resetState = getInitialOptionsState();
    return {
      ...resetState,
      // Restore OS selection
      osWindows: prevOptions.osWindows,
      osMac: prevOptions.osMac,
      osLinux: prevOptions.osLinux,
    };
  }, []);

  /**
   * Handle option change with complex logic
   */
  const handleOptionChange = useCallback((optionName) => {
    setOptions(prev => {
      let newOptions = { ...prev };
      
      // JavaCard product selection - only one can be selected (radio buttons)
      if (optionName === 'javaCardProductSatochip' || optionName === 'javaCardProductSeedkeeper' || optionName === 'javaCardProductSatodime') {
        const product = optionName === 'javaCardProductSatochip' ? 'satochip' : 
                       optionName === 'javaCardProductSeedkeeper' ? 'seedkeeper' : 'satodime';
        if (prev.javaCardProduct === product) {
          // Unselect if already selected
          newOptions.javaCardProduct = null;
        } else {
          // Select the product
          newOptions.javaCardProduct = product;
        }
      }
      // "Shortest path" auto-populates other checkboxes
      else if (optionName === 'shortestPath') {
        if (!prev.shortestPath) {
          // Reset all fields except OS, then set shortest path options
          const resetOptions = resetAllFieldsExceptOS(prev);
          newOptions = {
            ...resetOptions,
            shortestPath: true,
            buySeedSigner: true,
            downloadInstallBalena: true,
            flashSeedSignerUseRelease: true,
            assembleSeedSigner: true,
            flashSeedSigner: true,
            javaCardProduct: 'satochip',
            buyJavaCardReady: true,
            downloadBlueWallet: true
          };
        } else {
          // Disable shortest path and reset all except OS
          newOptions = resetAllFieldsExceptOS(prev);
        }
      }
      // "Dabbler" auto-populates other checkboxes (use release, verify hash)
      else if (optionName === 'dabbler') {
        if (!prev.dabbler) {
          // Reset all fields except OS, then set dabbler options
          const resetOptions = resetAllFieldsExceptOS(prev);
          newOptions = {
            ...resetOptions,
            dabbler: true,
            buySeedSigner: true,
            downloadInstallBalena: true,
            flashSeedSignerUseRelease: true,
            assembleSeedSigner: true,
            flashSeedSigner: true,
            verifySignature: true,
            javaCardProduct: 'satochip',
            buyJavaCardBlank: true,
            flashJavaCardViaSeedSigner: true
          };
        } else {
          // Disable dabbler and reset all except OS
          newOptions = resetAllFieldsExceptOS(prev);
        }
      }
      // "Rockstar dev" auto-populates other checkboxes (compile options + advanced verification)
      else if (optionName === 'rockstarDev') {
        if (!prev.rockstarDev) {
          // Reset all fields except OS, then set rockstar dev options
          const resetOptions = resetAllFieldsExceptOS(prev);
          newOptions = {
            ...resetOptions,
            rockstarDev: true,
            flashSeedSignerCompile: true,
            verifySignature: true,
            verifyBitcoinSignature: true,
            javaCardProduct: 'satochip',
            buyJavaCardBlank: true,
            flashJavaCardDesktopCompile: true
          };
        } else {
          // Disable rockstar dev and reset all except OS
          newOptions = resetAllFieldsExceptOS(prev);
        }
      }
      // OS selection - only one can be selected
      else if (optionName === 'osWindows' || optionName === 'osMac' || optionName === 'osLinux') {
        if (!prev[optionName]) {
          // Uncheck other OS options
          newOptions.osWindows = false;
          newOptions.osMac = false;
          newOptions.osLinux = false;
          // Check selected OS
          newOptions[optionName] = true;
        } else {
          // Uncheck if already selected (preserve other selections)
          newOptions[optionName] = false;
        }
      }
      // Flash SeedSigner subcategories - only one can be selected
      else if (optionName === 'flashSeedSignerUseRelease' || optionName === 'flashSeedSignerCompile') {
        if (!prev[optionName]) {
          // Uncheck other flash method
          newOptions.flashSeedSignerUseRelease = false;
          newOptions.flashSeedSignerCompile = false;
          // Check selected method
          newOptions[optionName] = true;
          // Disable shortcuts when manually selecting
          newOptions.shortestPath = false;
          newOptions.dabbler = false;
          newOptions.rockstarDev = false;
        } else {
          // Uncheck if already selected
          newOptions[optionName] = false;
          // If unchecking "Use release", also uncheck verify signature
          if (optionName === 'flashSeedSignerUseRelease') {
            newOptions.verifySignature = false;
          }
        }
      }
      // JavaCard card acquisition - Buy Ready vs Buy Blank (mutually exclusive)
      else if (optionName === 'buyJavaCardReady' || optionName === 'buyJavaCardBlank') {
        if (!prev[optionName]) {
          // Uncheck other acquisition method
          newOptions.buyJavaCardReady = false;
          newOptions.buyJavaCardBlank = false;
          // Check selected method
          newOptions[optionName] = true;
          // If buying ready card, disable all flashing options
          if (optionName === 'buyJavaCardReady') {
            newOptions.flashJavaCardViaSeedSigner = false;
            newOptions.flashJavaCardDesktopCompile = false;
            newOptions.flashJavaCardDesktopDownload = false;
            newOptions.verifySignatureJavaCardDesktop = false;
          }
          // Disable shortcuts when manually selecting
          newOptions.shortestPath = false;
          newOptions.dabbler = false;
          newOptions.rockstarDev = false;
        } else {
          // Uncheck if already selected
          newOptions[optionName] = false;
        }
      }
      // JavaCard flashing method - SeedSigner vs Desktop (mutually exclusive, only if blank card)
      else if (optionName === 'flashJavaCardViaSeedSigner') {
        if (!prev[optionName]) {
          // Uncheck desktop flashing methods
          newOptions.flashJavaCardDesktopCompile = false;
          newOptions.flashJavaCardDesktopDownload = false;
          newOptions.verifySignatureJavaCardDesktop = false;
          // Check selected method
          newOptions[optionName] = true;
          // Disable shortcuts when manually selecting
          newOptions.shortestPath = false;
          newOptions.dabbler = false;
          newOptions.rockstarDev = false;
        } else {
          // Uncheck if already selected
          newOptions[optionName] = false;
        }
      }
      // Flash JavaCard via desktop subcategories - only one can be selected
      else if (optionName === 'flashJavaCardDesktopCompile' || optionName === 'flashJavaCardDesktopDownload') {
        if (!prev[optionName]) {
          // Uncheck other flash method
          newOptions.flashJavaCardDesktopCompile = false;
          newOptions.flashJavaCardDesktopDownload = false;
          // Uncheck SeedSigner flashing
          newOptions.flashJavaCardViaSeedSigner = false;
          // Check selected method
          newOptions[optionName] = true;
          // Disable shortcuts when manually selecting
          newOptions.shortestPath = false;
          newOptions.dabbler = false;
          newOptions.rockstarDev = false;
        } else {
          // Uncheck if already selected
          newOptions[optionName] = false;
          // If unchecking "Use release", also uncheck verify signature
          if (optionName === 'flashJavaCardDesktopDownload') {
            newOptions.verifySignatureJavaCardDesktop = false;
          }
        }
      }
      // If selecting any other option, disable shortcuts
      else {
        newOptions.shortestPath = false;
        newOptions.dabbler = false;
        newOptions.rockstarDev = false;
        newOptions[optionName] = !prev[optionName];
      }
      
      return newOptions;
    });
  }, [resetAllFieldsExceptOS]);

  /**
   * Reset all options
   */
  const handleReset = useCallback(() => {
    setOptions(getInitialOptionsState());
  }, []);

  return {
    options,
    handleOptionChange,
    handleReset
  };
}

